/**
 * US Journey Builder, deterministic plan engine.
 *
 * Dollars are commercial allowed-amount bands for an initial treatment course
 * (about 8-24 weeks), plus patient out-of-pocket from published insurance
 * caps, plus travel/lodging from 2025-26 medical-center hotel ADRs.
 *
 * These are estimates to help families plan the next conversation with the hospital and insurer.
 */

export const JOURNEY_CURRENCY = 'USD';
export const JOURNEY_STORAGE_KEY = 'byonco_journey_last';

export const JOURNEY_SOURCES = [
  {
    id: 'cms-asp-2026',
    label: 'CMS ASP Payment Limits, July 2026',
    detail: 'Pembrolizumab (J9271) $60.645/mg → $12,129 per 200 mg dose.',
  },
  {
    id: 'aca-2026-oop',
    label: 'CMS / HHS 2026 ACA maximum out-of-pocket',
    detail: '$10,600 self-only / $21,200 family.',
  },
  {
    id: 'kff-ehbs-2024',
    label: 'KFF Employer Health Benefits Survey 2024',
    detail: 'Typical single-coverage deductible ~$1,800; average in-network OOP max about $4,100.',
  },
  {
    id: 'jama-2025-oop',
    label: 'JAMA Network Open 2025 (SEER-linked commercial claims)',
    detail: 'Privately insured patients under 65: +$593/month OOP for 6 months after diagnosis; higher at later stages.',
  },
  {
    id: 'ira-partd-2025',
    label: 'Inflation Reduction Act Medicare Part D cap',
    detail: '$2,000 annual out-of-pocket cap on Part D drugs (2025+).',
  },
  {
    id: 'acs-milliman',
    label: 'ACS CAN / Milliman Costs of Cancer (commercial allowed)',
    detail: 'First-year allowed amounts: breast ~$60k-$80k typical; lung and blood cancers substantially higher.',
  },
  {
    id: 'nci-centers',
    label: 'NCI Cancer Centers Program',
    detail: 'Hospitals are NCI-designated or academic network centres from the ByOnco US catalog.',
  },
];

export const RELATIONS = [
  { id: 'self', label: 'myself' },
  { id: 'spouse', label: 'my spouse / partner' },
  { id: 'parent', label: 'my parent' },
  { id: 'child', label: 'my child' },
  { id: 'sibling', label: 'my sibling' },
  { id: 'relative', label: 'another family member' },
  { id: 'friend', label: 'someone I care for' },
];

export const AGE_GROUPS = [
  { id: 'adult', label: 'an adult' },
  { id: 'child', label: 'a child (17 or under)' },
];

export const CANCER_TYPES = [
  { id: 'breast', label: 'breast cancer' },
  { id: 'lung', label: 'lung cancer' },
  { id: 'colorectal', label: 'colorectal cancer' },
  { id: 'prostate', label: 'prostate cancer' },
  { id: 'pancreatic', label: 'pancreatic cancer' },
  { id: 'ovarian', label: 'ovarian cancer' },
  { id: 'blood', label: 'leukemia or lymphoma' },
  { id: 'brain', label: 'brain tumor / glioblastoma' },
  { id: 'head_neck', label: 'head and neck cancer' },
  { id: 'melanoma', label: 'melanoma' },
  { id: 'other', label: 'another solid tumor' },
];

export const STAGES = [
  { id: 'I', label: 'Stage I' },
  { id: 'II', label: 'Stage II' },
  { id: 'III', label: 'Stage III' },
  { id: 'IV', label: 'Stage IV' },
  { id: 'unknown', label: 'stage not yet confirmed' },
];

export const US_STATES = [
  { id: 'AL', label: 'Alabama' }, { id: 'AK', label: 'Alaska' }, { id: 'AZ', label: 'Arizona' },
  { id: 'AR', label: 'Arkansas' }, { id: 'CA', label: 'California' }, { id: 'CO', label: 'Colorado' },
  { id: 'CT', label: 'Connecticut' }, { id: 'DC', label: 'Washington, D.C.' }, { id: 'DE', label: 'Delaware' },
  { id: 'FL', label: 'Florida' }, { id: 'GA', label: 'Georgia' }, { id: 'HI', label: 'Hawaii' },
  { id: 'ID', label: 'Idaho' }, { id: 'IL', label: 'Illinois' }, { id: 'IN', label: 'Indiana' },
  { id: 'IA', label: 'Iowa' }, { id: 'KS', label: 'Kansas' }, { id: 'KY', label: 'Kentucky' },
  { id: 'LA', label: 'Louisiana' }, { id: 'ME', label: 'Maine' }, { id: 'MD', label: 'Maryland' },
  { id: 'MA', label: 'Massachusetts' }, { id: 'MI', label: 'Michigan' }, { id: 'MN', label: 'Minnesota' },
  { id: 'MS', label: 'Mississippi' }, { id: 'MO', label: 'Missouri' }, { id: 'MT', label: 'Montana' },
  { id: 'NE', label: 'Nebraska' }, { id: 'NV', label: 'Nevada' }, { id: 'NH', label: 'New Hampshire' },
  { id: 'NJ', label: 'New Jersey' }, { id: 'NM', label: 'New Mexico' }, { id: 'NY', label: 'New York' },
  { id: 'NC', label: 'North Carolina' }, { id: 'ND', label: 'North Dakota' }, { id: 'OH', label: 'Ohio' },
  { id: 'OK', label: 'Oklahoma' }, { id: 'OR', label: 'Oregon' }, { id: 'PA', label: 'Pennsylvania' },
  { id: 'RI', label: 'Rhode Island' }, { id: 'SC', label: 'South Carolina' }, { id: 'SD', label: 'South Dakota' },
  { id: 'TN', label: 'Tennessee' }, { id: 'TX', label: 'Texas' }, { id: 'UT', label: 'Utah' },
  { id: 'VT', label: 'Vermont' }, { id: 'VA', label: 'Virginia' }, { id: 'WA', label: 'Washington' },
  { id: 'WV', label: 'West Virginia' }, { id: 'WI', label: 'Wisconsin' }, { id: 'WY', label: 'Wyoming' },
];

export const US_CITIES = [
  { city: 'New York', state: 'NY' }, { city: 'Brooklyn', state: 'NY' }, { city: 'Buffalo', state: 'NY' },
  { city: 'Los Angeles', state: 'CA' }, { city: 'San Francisco', state: 'CA' }, { city: 'San Diego', state: 'CA' },
  { city: 'San Jose', state: 'CA' }, { city: 'Palo Alto', state: 'CA' }, { city: 'Sacramento', state: 'CA' },
  { city: 'Houston', state: 'TX' }, { city: 'Dallas', state: 'TX' }, { city: 'Austin', state: 'TX' },
  { city: 'San Antonio', state: 'TX' }, { city: 'Fort Worth', state: 'TX' },
  { city: 'Chicago', state: 'IL' }, { city: 'Phoenix', state: 'AZ' }, { city: 'Scottsdale', state: 'AZ' },
  { city: 'Philadelphia', state: 'PA' }, { city: 'Pittsburgh', state: 'PA' },
  { city: 'Boston', state: 'MA' }, { city: 'Cambridge', state: 'MA' },
  { city: 'Miami', state: 'FL' }, { city: 'Tampa', state: 'FL' }, { city: 'Orlando', state: 'FL' },
  { city: 'Jacksonville', state: 'FL' }, { city: 'Atlanta', state: 'GA' },
  { city: 'Seattle', state: 'WA' }, { city: 'Portland', state: 'OR' },
  { city: 'Denver', state: 'CO' }, { city: 'Salt Lake City', state: 'UT' },
  { city: 'Minneapolis', state: 'MN' }, { city: 'Rochester', state: 'MN' },
  { city: 'Detroit', state: 'MI' }, { city: 'Ann Arbor', state: 'MI' },
  { city: 'Cleveland', state: 'OH' }, { city: 'Columbus', state: 'OH' }, { city: 'Cincinnati', state: 'OH' },
  { city: 'Nashville', state: 'TN' }, { city: 'Memphis', state: 'TN' },
  { city: 'Charlotte', state: 'NC' }, { city: 'Durham', state: 'NC' }, { city: 'Raleigh', state: 'NC' },
  { city: 'Chapel Hill', state: 'NC' }, { city: 'Baltimore', state: 'MD' },
  { city: 'Washington', state: 'DC' }, { city: 'Arlington', state: 'VA' }, { city: 'Richmond', state: 'VA' },
  { city: 'Newark', state: 'NJ' }, { city: 'New Brunswick', state: 'NJ' },
  { city: 'New Haven', state: 'CT' }, { city: 'Providence', state: 'RI' },
  { city: 'St. Louis', state: 'MO' }, { city: 'Kansas City', state: 'MO' },
  { city: 'Indianapolis', state: 'IN' }, { city: 'Milwaukee', state: 'WI' }, { city: 'Madison', state: 'WI' },
  { city: 'New Orleans', state: 'LA' }, { city: 'Birmingham', state: 'AL' },
  { city: 'Oklahoma City', state: 'OK' }, { city: 'Albuquerque', state: 'NM' },
  { city: 'Las Vegas', state: 'NV' }, { city: 'Honolulu', state: 'HI' },
  { city: 'Anchorage', state: 'AK' }, { city: 'Boise', state: 'ID' },
];

export function searchCities(query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return US_CITIES.slice(0, 8);
  return US_CITIES.filter((row) => (
    row.city.toLowerCase().includes(q)
    || row.state.toLowerCase() === q
    || `${row.city}, ${row.state}`.toLowerCase().includes(q)
  )).slice(0, 10);
}

export function searchOptions(options, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return options;
  return options.filter((row) => row.label.toLowerCase().includes(q) || String(row.id).toLowerCase().includes(q));
}

export const INTAKE_FIELDS = [
  ['relation', 'Who this is for'],
  ['ageGroup', 'Whether the patient is an adult or a child'],
  ['cancerType', 'Diagnosis'],
  ['stage', 'Stage'],
  ['city', 'Home city'],
  ['state', 'State'],
  ['destination', 'Where treatment should happen'],
  ['insurance', 'Coverage'],
  ['lodging', 'Housing during treatment'],
  ['companions', 'Who is traveling'],
  ['urgency', 'Timing'],
];

export function missingIntakeFields(intake) {
  return INTAKE_FIELDS
    .filter(([key]) => !String(intake?.[key] || '').trim())
    .map(([, label]) => label);
}

export const DESTINATIONS = [
  { id: 'match', label: 'wherever the best-fit NCI center is' },
  { id: 'nearby', label: 'as close to home as possible' },
  { id: 'New York City', label: 'New York City' },
  { id: 'Houston', label: 'Houston' },
  { id: 'Boston', label: 'Boston' },
  { id: 'Rochester (MN)', label: 'Rochester, Minnesota (Mayo Clinic)' },
  { id: 'Baltimore', label: 'Baltimore' },
  { id: 'Chicago', label: 'Chicago' },
  { id: 'Los Angeles', label: 'Los Angeles' },
  { id: 'San Francisco', label: 'San Francisco Bay Area' },
  { id: 'Atlanta', label: 'Atlanta' },
  { id: 'Tampa', label: 'Tampa' },
  { id: 'Seattle', label: 'Seattle' },
  { id: 'Phoenix', label: 'Phoenix' },
  { id: 'Philadelphia', label: 'Philadelphia' },
  { id: 'Durham', label: 'Durham / Research Triangle' },
];

export const INSURANCE_TYPES = [
  { id: 'employer_ppo', label: 'employer PPO / POS' },
  { id: 'employer_hmo', label: 'employer HMO / EPO' },
  { id: 'marketplace', label: 'ACA marketplace coverage' },
  { id: 'medicare', label: 'Original Medicare' },
  { id: 'medicare_advantage', label: 'Medicare Advantage' },
  { id: 'medicaid', label: 'Medicaid' },
  { id: 'tricare', label: 'TRICARE / VA' },
  { id: 'uninsured', label: 'uninsured / self-pay' },
];

export const LODGING_NEEDS = [
  { id: 'home', label: 'we can stay at home' },
  { id: 'family', label: 'we have family housing near the hospital' },
  { id: 'lodging', label: 'we need lodging near the hospital' },
];

export const COMPANIONS = [
  { id: '1', label: 'the patient only' },
  { id: '2', label: 'the patient plus one caregiver' },
];

export const URGENCY = [
  { id: 'immediately', label: 'as soon as possible' },
  { id: 'within_month', label: 'within a month' },
  { id: 'flexible', label: 'we can plan over the next few months' },
];

/** Mid-market ADR near major cancer centers, USD / night, 2025-26. */
const LODGING_MARKETS = {
  'New York City': { es: 195, mid: 285, premium: 430, state: 'NY' },
  Houston: { es: 115, mid: 165, premium: 275, state: 'TX' },
  Boston: { es: 175, mid: 255, premium: 390, state: 'MA' },
  'Rochester (MN)': { es: 105, mid: 155, premium: 240, state: 'MN' },
  Baltimore: { es: 125, mid: 175, premium: 280, state: 'MD' },
  Chicago: { es: 145, mid: 195, premium: 310, state: 'IL' },
  'Los Angeles': { es: 170, mid: 230, premium: 360, state: 'CA' },
  'San Francisco': { es: 195, mid: 290, premium: 430, state: 'CA' },
  'Palo Alto': { es: 210, mid: 305, premium: 450, state: 'CA' },
  Atlanta: { es: 120, mid: 165, premium: 260, state: 'GA' },
  Tampa: { es: 115, mid: 160, premium: 250, state: 'FL' },
  Miami: { es: 155, mid: 215, premium: 340, state: 'FL' },
  Seattle: { es: 150, mid: 200, premium: 320, state: 'WA' },
  Phoenix: { es: 125, mid: 165, premium: 260, state: 'AZ' },
  Philadelphia: { es: 140, mid: 185, premium: 295, state: 'PA' },
  Durham: { es: 110, mid: 145, premium: 230, state: 'NC' },
  'Chapel Hill': { es: 110, mid: 145, premium: 230, state: 'NC' },
  Cleveland: { es: 115, mid: 150, premium: 240, state: 'OH' },
  'Ann Arbor': { es: 120, mid: 160, premium: 250, state: 'MI' },
  Nashville: { es: 130, mid: 175, premium: 280, state: 'TN' },
  Dallas: { es: 125, mid: 170, premium: 270, state: 'TX' },
  Denver: { es: 140, mid: 185, premium: 290, state: 'CO' },
  'Salt Lake City': { es: 115, mid: 155, premium: 250, state: 'UT' },
  Portland: { es: 135, mid: 180, premium: 285, state: 'OR' },
  Memphis: { es: 95, mid: 135, premium: 210, state: 'TN' },
  Duarte: { es: 150, mid: 205, premium: 320, state: 'CA' },
  Birmingham: { es: 100, mid: 140, premium: 220, state: 'AL' },
  default: { es: 120, mid: 165, premium: 260, state: null },
};

const NCI = 'NCI-Designated Comprehensive Cancer Center';
const PED = 'Pediatric Oncology Center';

export const HOSPITALS = {
  'msk-nyc': { id: 'msk-nyc', name: 'Memorial Sloan Kettering Cancer Center', city: 'New York City', state: 'NY', region: 'northeast', designation: NCI, note: 'Highest-volume NCI comprehensive center in New York. Strong thoracic, breast, and rare-cancer programs.', website: 'https://www.mskcc.org', tier: 'destination' },
  'nyu-perlmutter': { id: 'nyu-perlmutter', name: 'NYU Langone Perlmutter Cancer Center', city: 'New York City', state: 'NY', region: 'northeast', designation: NCI, note: 'NCI comprehensive center with a large thoracic and mesothelioma program.', website: 'https://nyulangone.org/locations/perlmutter-cancer-center', tier: 'major' },
  'columbia-hiccc': { id: 'columbia-hiccc', name: 'Herbert Irving Comprehensive Cancer Center', city: 'New York City', state: 'NY', region: 'northeast', designation: NCI, note: 'Columbia / NewYork-Presbyterian NCI comprehensive center.', website: 'https://www.cancer.columbia.edu', tier: 'major' },
  'dfci-boston': { id: 'dfci-boston', name: 'Dana-Farber Cancer Institute', city: 'Boston', state: 'MA', region: 'northeast', designation: NCI, note: 'Harvard-affiliated NCI comprehensive center; adult care with Brigham and Women’s.', website: 'https://www.dana-farber.org', tier: 'destination' },
  'mgh-cancer-boston': { id: 'mgh-cancer-boston', name: 'Massachusetts General Hospital Cancer Center', city: 'Boston', state: 'MA', region: 'northeast', designation: 'Dana-Farber/Harvard Cancer Center consortium', note: 'Part of the DF/HCC NCI consortium; proton therapy and thoracic oncology.', website: 'https://www.massgeneral.org/cancer-center', tier: 'major' },
  'hopkins-baltimore': { id: 'hopkins-baltimore', name: 'Johns Hopkins Sidney Kimmel Comprehensive Cancer Center', city: 'Baltimore', state: 'MD', region: 'midatlantic', designation: NCI, note: 'NCI comprehensive center with national referral programs across solid tumors.', website: 'https://www.hopkinsmedicine.org/kimmel-cancer-center', tier: 'destination' },
  'abramson-penn': { id: 'abramson-penn', name: 'Abramson Cancer Center, University of Pennsylvania', city: 'Philadelphia', state: 'PA', region: 'northeast', designation: NCI, note: 'Birthplace of approved CD19 CAR-T; proton therapy on site.', website: 'https://www.pennmedicine.org/cancer', tier: 'major' },
  'fox-chase-philadelphia': { id: 'fox-chase-philadelphia', name: 'Fox Chase Cancer Center', city: 'Philadelphia', state: 'PA', region: 'northeast', designation: NCI, note: 'Freestanding NCI comprehensive cancer center.', website: 'https://www.foxchase.org', tier: 'regional' },
  'yale-cancer-new-haven': { id: 'yale-cancer-new-haven', name: 'Yale Cancer Center', city: 'New Haven', state: 'CT', region: 'northeast', designation: NCI, note: 'NCI comprehensive center with thoracic and melanoma depth.', website: 'https://www.yalecancercenter.org', tier: 'regional' },
  'rutgers-cinj': { id: 'rutgers-cinj', name: 'Rutgers Cancer Institute of New Jersey', city: 'New Brunswick', state: 'NJ', region: 'northeast', designation: NCI, note: 'New Jersey’s only NCI-designated comprehensive cancer center.', website: 'https://www.cinj.org', tier: 'regional' },
  'roswell-park-buffalo': { id: 'roswell-park-buffalo', name: 'Roswell Park Comprehensive Cancer Center', city: 'Buffalo', state: 'NY', region: 'northeast', designation: NCI, note: 'The first institution in the world founded specifically for cancer research.', website: 'https://www.roswellpark.org', tier: 'regional' },
  'dartmouth-cancer': { id: 'dartmouth-cancer', name: 'Dartmouth Cancer Center', city: 'Lebanon', state: 'NH', region: 'northeast', designation: NCI, note: 'NCI comprehensive center serving northern New England.', website: 'https://cancer.dartmouth.edu', tier: 'regional' },
  'lombardi-dc': { id: 'lombardi-dc', name: 'Georgetown Lombardi Comprehensive Cancer Center', city: 'Washington', state: 'DC', region: 'midatlantic', designation: NCI, note: 'NCI comprehensive center for the D.C. region.', website: 'https://www.lombardi.georgetown.edu', tier: 'regional' },
  'md-anderson-houston': { id: 'md-anderson-houston', name: 'MD Anderson Cancer Center', city: 'Houston', state: 'TX', region: 'southcentral', designation: NCI, note: 'One of the largest dedicated cancer hospitals in the US. National destination for complex and rare disease.', website: 'https://www.mdanderson.org', tier: 'destination' },
  'ut-southwestern-simmons': { id: 'ut-southwestern-simmons', name: 'UT Southwestern Harold C. Simmons Comprehensive Cancer Center', city: 'Dallas', state: 'TX', region: 'southcentral', designation: NCI, note: 'North Texas NCI comprehensive center.', website: 'https://utswmed.org/cancer', tier: 'major' },
  'mays-san-antonio': { id: 'mays-san-antonio', name: 'Mays Cancer Center, UT Health San Antonio', city: 'San Antonio', state: 'TX', region: 'southcentral', designation: NCI, note: 'South Texas NCI comprehensive center.', website: 'https://cancer.uthscsa.edu', tier: 'regional' },
  'mayo-rochester': { id: 'mayo-rochester', name: 'Mayo Clinic Comprehensive Cancer Center', city: 'Rochester (MN)', state: 'MN', region: 'midwest', designation: NCI, note: 'Integrated destination campus: diagnostics, surgery, and medical oncology under one system.', website: 'https://www.mayoclinic.org', tier: 'destination' },
  'mayo-arizona': { id: 'mayo-arizona', name: 'Mayo Clinic Comprehensive Cancer Center, Arizona', city: 'Phoenix', state: 'AZ', region: 'mountain', designation: NCI, note: 'Mayo Clinic’s Arizona comprehensive cancer campus.', website: 'https://www.mayoclinic.org', tier: 'destination' },
  'masonic-minneapolis': { id: 'masonic-minneapolis', name: 'Masonic Cancer Center, University of Minnesota', city: 'Minneapolis', state: 'MN', region: 'midwest', designation: NCI, note: 'NCI comprehensive center with a major transplant program.', website: 'https://cancer.umn.edu', tier: 'regional' },
  'lurie-northwestern': { id: 'lurie-northwestern', name: 'Robert H. Lurie Comprehensive Cancer Center of Northwestern University', city: 'Chicago', state: 'IL', region: 'midwest', designation: NCI, note: 'Downtown Chicago NCI comprehensive center.', website: 'https://www.cancer.northwestern.edu', tier: 'major' },
  'uchicago-cancer': { id: 'uchicago-cancer', name: 'University of Chicago Comprehensive Cancer Center', city: 'Chicago', state: 'IL', region: 'midwest', designation: NCI, note: 'South Side Chicago NCI comprehensive center.', website: 'https://cancer.uchicago.edu', tier: 'major' },
  'siteman-stlouis': { id: 'siteman-stlouis', name: 'Siteman Cancer Center', city: 'St. Louis', state: 'MO', region: 'midwest', designation: NCI, note: 'Washington University / Barnes-Jewish NCI comprehensive center.', website: 'https://siteman.wustl.edu', tier: 'major' },
  'rogel-ann-arbor': { id: 'rogel-ann-arbor', name: 'University of Michigan Rogel Cancer Center', city: 'Ann Arbor', state: 'MI', region: 'midwest', designation: NCI, note: 'NCI comprehensive center serving Michigan and the Great Lakes.', website: 'https://www.rogelcancercenter.org', tier: 'regional' },
  'karmanos-detroit': { id: 'karmanos-detroit', name: 'Barbara Ann Karmanos Cancer Institute', city: 'Detroit', state: 'MI', region: 'midwest', designation: NCI, note: 'Freestanding NCI comprehensive cancer institute.', website: 'https://www.karmanos.org', tier: 'regional' },
  'osu-james-columbus': { id: 'osu-james-columbus', name: 'The James Cancer Hospital (Ohio State)', city: 'Columbus', state: 'OH', region: 'midwest', designation: NCI, note: 'One of the largest dedicated cancer hospitals in the Midwest.', website: 'https://cancer.osu.edu', tier: 'major' },
  'case-cleveland': { id: 'case-cleveland', name: 'Case Comprehensive Cancer Center', city: 'Cleveland', state: 'OH', region: 'midwest', designation: NCI, note: 'Consortium of Case Western, University Hospitals Seidman, and Cleveland Clinic Taussig.', website: 'https://case.edu/cancer', tier: 'major' },
  'iu-simon-indianapolis': { id: 'iu-simon-indianapolis', name: 'IU Simon Comprehensive Cancer Center', city: 'Indianapolis', state: 'IN', region: 'midwest', designation: NCI, note: 'Where cisplatin-based curative therapy for testicular cancer was developed.', website: 'https://www.cancer.iu.edu', tier: 'regional' },
  'winship-atlanta': { id: 'winship-atlanta', name: 'Winship Cancer Institute of Emory University', city: 'Atlanta', state: 'GA', region: 'southeast', designation: NCI, note: 'Georgia’s NCI comprehensive cancer center.', website: 'https://winshipcancer.emory.edu', tier: 'major' },
  'moffitt-tampa': { id: 'moffitt-tampa', name: 'Moffitt Cancer Center', city: 'Tampa', state: 'FL', region: 'southeast', designation: NCI, note: 'Florida’s NCI comprehensive center; cellular immunotherapy program.', website: 'https://www.moffitt.org', tier: 'major' },
  'sylvester-miami': { id: 'sylvester-miami', name: 'Sylvester Comprehensive Cancer Center', city: 'Miami', state: 'FL', region: 'southeast', designation: NCI, note: 'South Florida NCI comprehensive center; large Spanish-speaking population.', website: 'https://umiamihealth.org/sylvester', tier: 'regional' },
  'duke-cancer-durham': { id: 'duke-cancer-durham', name: 'Duke Cancer Institute', city: 'Durham', state: 'NC', region: 'southeast', designation: NCI, note: 'NCI comprehensive center in the Research Triangle.', website: 'https://www.dukecancerinstitute.org', tier: 'major' },
  'unc-lineberger': { id: 'unc-lineberger', name: 'UNC Lineberger Comprehensive Cancer Center', city: 'Chapel Hill', state: 'NC', region: 'southeast', designation: NCI, note: 'NCI comprehensive center at UNC Health.', website: 'https://unclineberger.org', tier: 'regional' },
  'vanderbilt-ingram': { id: 'vanderbilt-ingram', name: 'Vanderbilt-Ingram Cancer Center', city: 'Nashville', state: 'TN', region: 'southeast', designation: NCI, note: 'NCI comprehensive center for Tennessee and the mid-South.', website: 'https://www.vicc.org', tier: 'major' },
  'oneal-birmingham': { id: 'oneal-birmingham', name: "O'Neal Comprehensive Cancer Center at UAB", city: 'Birmingham', state: 'AL', region: 'southeast', designation: NCI, note: 'Alabama’s NCI comprehensive cancer center.', website: 'https://www.uab.edu/onealcancercenter', tier: 'regional' },
  'hollings-charleston': { id: 'hollings-charleston', name: 'MUSC Hollings Cancer Center', city: 'Charleston', state: 'SC', region: 'southeast', designation: NCI, note: 'South Carolina’s NCI comprehensive cancer center.', website: 'https://hollingscancercenter.musc.edu', tier: 'regional' },
  'massey-richmond': { id: 'massey-richmond', name: 'VCU Massey Comprehensive Cancer Center', city: 'Richmond', state: 'VA', region: 'midatlantic', designation: NCI, note: 'Virginia’s NCI comprehensive cancer center in Richmond.', website: 'https://www.masseycancercenter.org', tier: 'regional' },
  'stanford-cancer': { id: 'stanford-cancer', name: 'Stanford Cancer Institute', city: 'Palo Alto', state: 'CA', region: 'pacific', designation: NCI, note: 'NCI comprehensive center; immunotherapy and early-phase trials.', website: 'https://med.stanford.edu/cancer.html', tier: 'destination' },
  'ucsf-helen-diller': { id: 'ucsf-helen-diller', name: 'UCSF Helen Diller Family Comprehensive Cancer Center', city: 'San Francisco', state: 'CA', region: 'pacific', designation: NCI, note: 'NCI comprehensive center for the Bay Area.', website: 'https://cancer.ucsf.edu', tier: 'major' },
  'ucla-jonsson': { id: 'ucla-jonsson', name: 'UCLA Jonsson Comprehensive Cancer Center', city: 'Los Angeles', state: 'CA', region: 'pacific', designation: NCI, note: 'Westwood NCI comprehensive center.', website: 'https://cancer.ucla.edu', tier: 'major' },
  'city-of-hope-duarte': { id: 'city-of-hope-duarte', name: 'City of Hope Comprehensive Cancer Center', city: 'Duarte', state: 'CA', region: 'pacific', designation: NCI, note: 'Among the largest bone-marrow and stem-cell transplant programs in the US.', website: 'https://www.cityofhope.org', tier: 'destination' },
  'moores-ucsd': { id: 'moores-ucsd', name: 'UC San Diego Moores Cancer Center', city: 'San Diego', state: 'CA', region: 'pacific', designation: NCI, note: 'Southern California NCI comprehensive center.', website: 'https://health.ucsd.edu/cancer', tier: 'regional' },
  'fred-hutch-seattle': { id: 'fred-hutch-seattle', name: 'Fred Hutchinson Cancer Center', city: 'Seattle', state: 'WA', region: 'pacific', designation: NCI, note: 'Origin of allogeneic bone-marrow transplantation as curative therapy.', website: 'https://www.fredhutch.org', tier: 'major' },
  'ohsu-knight': { id: 'ohsu-knight', name: 'OHSU Knight Cancer Institute', city: 'Portland', state: 'OR', region: 'pacific', designation: NCI, note: 'Home of the imatinib program that transformed CML and GIST.', website: 'https://www.ohsu.edu/knight-cancer-institute', tier: 'regional' },
  'huntsman-slc': { id: 'huntsman-slc', name: 'Huntsman Cancer Institute', city: 'Salt Lake City', state: 'UT', region: 'mountain', designation: NCI, note: 'Mountain West NCI comprehensive center; hereditary cancer genetics.', website: 'https://healthcare.utah.edu/huntsmancancerinstitute', tier: 'regional' },
  'cu-cancer-denver': { id: 'cu-cancer-denver', name: 'University of Colorado Cancer Center', city: 'Denver', state: 'CO', region: 'mountain', designation: NCI, note: 'NCI comprehensive center for the Rockies.', website: 'https://medschool.cuanschutz.edu/colorado-cancer-center', tier: 'regional' },
  'uh-cancer-honolulu': { id: 'uh-cancer-honolulu', name: 'University of Hawaii Cancer Center', city: 'Honolulu', state: 'HI', region: 'pacific', designation: 'NCI-Designated Cancer Center', note: 'NCI clinical cancer center serving Hawaii and the Pacific.', website: 'https://www.uhcancercenter.org', tier: 'regional' },
  'st-jude-memphis': { id: 'st-jude-memphis', name: "St. Jude Children's Research Hospital", city: 'Memphis', state: 'TN', region: 'southeast', designation: PED, note: 'Families are never billed for treatment, travel, or housing.', website: 'https://www.stjude.org', tier: 'destination', pediatric: true, neverBillsFamilies: true },
  'chop-philadelphia': { id: 'chop-philadelphia', name: "Children's Hospital of Philadelphia", city: 'Philadelphia', state: 'PA', region: 'northeast', designation: PED, note: 'Major freestanding children’s oncology program, including retinoblastoma.', website: 'https://www.chop.edu', tier: 'major', pediatric: true },
  'boston-childrens': { id: 'boston-childrens', name: "Boston Children's / Dana-Farber pediatric program", city: 'Boston', state: 'MA', region: 'northeast', designation: PED, note: 'Joint Dana-Farber / Boston Children’s cancer and blood disorders center.', website: 'https://www.dana-farber.org/boston-childrens', tier: 'destination', pediatric: true },
  'texas-childrens-houston': { id: 'texas-childrens-houston', name: "Texas Children's Cancer and Hematology Center", city: 'Houston', state: 'TX', region: 'southcentral', designation: PED, note: 'One of the largest pediatric cancer programs in the US.', website: 'https://www.texaschildrens.org', tier: 'major', pediatric: true },
  'seattle-childrens': { id: 'seattle-childrens', name: "Seattle Children's Hospital", city: 'Seattle', state: 'WA', region: 'pacific', designation: PED, note: 'Pacific Northwest pediatric oncology and BMT.', website: 'https://www.seattlechildrens.org', tier: 'regional', pediatric: true },
};

const STATE_REGION = {
  ME: 'northeast', NH: 'northeast', VT: 'northeast', MA: 'northeast', RI: 'northeast', CT: 'northeast',
  NY: 'northeast', NJ: 'northeast', PA: 'northeast',
  MD: 'midatlantic', DC: 'midatlantic', DE: 'midatlantic', VA: 'midatlantic', WV: 'midatlantic',
  NC: 'southeast', SC: 'southeast', GA: 'southeast', FL: 'southeast', AL: 'southeast', TN: 'southeast',
  KY: 'southeast', MS: 'southeast',
  TX: 'southcentral', OK: 'southcentral', AR: 'southcentral', LA: 'southcentral',
  OH: 'midwest', MI: 'midwest', IN: 'midwest', IL: 'midwest', WI: 'midwest', MN: 'midwest',
  IA: 'midwest', MO: 'midwest', KS: 'midwest', NE: 'midwest', ND: 'midwest', SD: 'midwest',
  CO: 'mountain', UT: 'mountain', AZ: 'mountain', NM: 'mountain', NV: 'mountain', ID: 'mountain',
  MT: 'mountain', WY: 'mountain',
  CA: 'pacific', OR: 'pacific', WA: 'pacific', HI: 'pacific', AK: 'pacific',
};

const STATE_VALUE = {
  AL: 'oneal-birmingham', AK: 'fred-hutch-seattle', AZ: 'mayo-arizona', AR: 'vanderbilt-ingram',
  CA: 'ucla-jonsson', CO: 'cu-cancer-denver', CT: 'yale-cancer-new-haven', DC: 'lombardi-dc',
  DE: 'hopkins-baltimore', FL: 'moffitt-tampa', GA: 'winship-atlanta', HI: 'uh-cancer-honolulu',
  ID: 'huntsman-slc', IL: 'lurie-northwestern', IN: 'iu-simon-indianapolis', IA: 'masonic-minneapolis',
  KS: 'siteman-stlouis', KY: 'vanderbilt-ingram', LA: 'md-anderson-houston', ME: 'dartmouth-cancer',
  MD: 'hopkins-baltimore', MA: 'dfci-boston', MI: 'rogel-ann-arbor', MN: 'mayo-rochester',
  MS: 'oneal-birmingham', MO: 'siteman-stlouis', MT: 'cu-cancer-denver', NE: 'siteman-stlouis',
  NV: 'mayo-arizona', NH: 'dartmouth-cancer', NJ: 'rutgers-cinj', NM: 'mayo-arizona',
  NY: 'msk-nyc', NC: 'duke-cancer-durham', ND: 'masonic-minneapolis', OH: 'osu-james-columbus',
  OK: 'ut-southwestern-simmons', OR: 'ohsu-knight', PA: 'abramson-penn', RI: 'dfci-boston',
  SC: 'hollings-charleston', SD: 'masonic-minneapolis', TN: 'vanderbilt-ingram', TX: 'md-anderson-houston',
  UT: 'huntsman-slc', VT: 'dartmouth-cancer', VA: 'massey-richmond', WA: 'fred-hutch-seattle',
  WV: 'massey-richmond', WI: 'lurie-northwestern', WY: 'cu-cancer-denver',
};

const CITY_HINTS = [
  { test: /new york|manhattan|brooklyn|queens|bronx|nyc/i, id: 'msk-nyc' },
  { test: /houston/i, id: 'md-anderson-houston' },
  { test: /dallas|fort worth|plano/i, id: 'ut-southwestern-simmons' },
  { test: /san antonio/i, id: 'mays-san-antonio' },
  { test: /boston|cambridge|brookline/i, id: 'dfci-boston' },
  { test: /rochester/i, id: 'mayo-rochester' },
  { test: /baltimore/i, id: 'hopkins-baltimore' },
  { test: /philadelphia|philly/i, id: 'abramson-penn' },
  { test: /chicago/i, id: 'lurie-northwestern' },
  { test: /los angeles|\bla\b|santa monica/i, id: 'ucla-jonsson' },
  { test: /san francisco|oakland|berkeley/i, id: 'ucsf-helen-diller' },
  { test: /palo alto|stanford|san jose|mountain view/i, id: 'stanford-cancer' },
  { test: /san diego/i, id: 'moores-ucsd' },
  { test: /seattle/i, id: 'fred-hutch-seattle' },
  { test: /portland/i, id: 'ohsu-knight' },
  { test: /atlanta/i, id: 'winship-atlanta' },
  { test: /tampa|st\.? petersburg/i, id: 'moffitt-tampa' },
  { test: /miami|fort lauderdale/i, id: 'sylvester-miami' },
  { test: /phoenix|scottsdale/i, id: 'mayo-arizona' },
  { test: /durham|raleigh|chapel hill/i, id: 'duke-cancer-durham' },
  { test: /nashville/i, id: 'vanderbilt-ingram' },
  { test: /cleveland/i, id: 'case-cleveland' },
  { test: /columbus/i, id: 'osu-james-columbus' },
  { test: /ann arbor|detroit/i, id: 'rogel-ann-arbor' },
  { test: /st\.? louis/i, id: 'siteman-stlouis' },
  { test: /minneapolis|st\.? paul/i, id: 'masonic-minneapolis' },
  { test: /salt lake/i, id: 'huntsman-slc' },
  { test: /denver|aurora/i, id: 'cu-cancer-denver' },
  { test: /honolulu/i, id: 'uh-cancer-honolulu' },
  { test: /buffalo/i, id: 'roswell-park-buffalo' },
  { test: /new haven/i, id: 'yale-cancer-new-haven' },
  { test: /memphis/i, id: 'st-jude-memphis' },
];

const FLAGSHIP = {
  northeast: 'msk-nyc',
  midatlantic: 'hopkins-baltimore',
  southeast: 'winship-atlanta',
  southcentral: 'md-anderson-houston',
  midwest: 'mayo-rochester',
  mountain: 'mayo-arizona',
  pacific: 'stanford-cancer',
};

const BALANCED = {
  northeast: 'dfci-boston',
  midatlantic: 'abramson-penn',
  southeast: 'moffitt-tampa',
  southcentral: 'ut-southwestern-simmons',
  midwest: 'lurie-northwestern',
  mountain: 'cu-cancer-denver',
  pacific: 'ucsf-helen-diller',
};

const PEDIATRIC_FLAGSHIP = 'st-jude-memphis';
const PEDIATRIC_BALANCED = {
  northeast: 'chop-philadelphia',
  midatlantic: 'chop-philadelphia',
  southeast: 'st-jude-memphis',
  southcentral: 'texas-childrens-houston',
  midwest: 'chop-philadelphia',
  mountain: 'seattle-childrens',
  pacific: 'seattle-childrens',
};
const PEDIATRIC_VALUE = {
  northeast: 'boston-childrens',
  midatlantic: 'chop-philadelphia',
  southeast: 'st-jude-memphis',
  southcentral: 'texas-childrens-houston',
  midwest: 'chop-philadelphia',
  mountain: 'seattle-childrens',
  pacific: 'seattle-childrens',
};

const CITY_DESTINATION = {
  'New York City': ['nyu-perlmutter', 'columbia-hiccc', 'msk-nyc'],
  Houston: ['mays-san-antonio', 'ut-southwestern-simmons', 'md-anderson-houston'],
  Boston: ['mgh-cancer-boston', 'dfci-boston', 'msk-nyc'],
  'Rochester (MN)': ['masonic-minneapolis', 'lurie-northwestern', 'mayo-rochester'],
  Baltimore: ['lombardi-dc', 'abramson-penn', 'hopkins-baltimore'],
  Chicago: ['rogel-ann-arbor', 'uchicago-cancer', 'lurie-northwestern'],
  'Los Angeles': ['ucla-jonsson', 'city-of-hope-duarte', 'stanford-cancer'],
  'San Francisco': ['ucsf-helen-diller', 'stanford-cancer', 'city-of-hope-duarte'],
  Atlanta: ['winship-atlanta', 'duke-cancer-durham', 'moffitt-tampa'],
  Tampa: ['moffitt-tampa', 'sylvester-miami', 'winship-atlanta'],
  Seattle: ['fred-hutch-seattle', 'ohsu-knight', 'stanford-cancer'],
  Phoenix: ['mayo-arizona', 'huntsman-slc', 'city-of-hope-duarte'],
  Philadelphia: ['fox-chase-philadelphia', 'abramson-penn', 'msk-nyc'],
  Durham: ['unc-lineberger', 'duke-cancer-durham', 'winship-atlanta'],
};

/**
 * Commercial allowed amounts for an initial course.
 * Lower bound: surgery / standard chemo-radiation without novel biologics.
 * Upper bound: includes typical immunotherapy / targeted-therapy drug spend
 * where NCCN commonly uses it (stage III-IV).
 */
const PROTOCOLS = {
  breast: {
    I: { label: 'Breast-conserving surgery + adjuvant radiation ± endocrine therapy', weeks: 6, medical: [28000, 52000], bullets: ['Lumpectomy or mastectomy at an NCI center', 'Whole-breast radiation (3-6 weeks) when indicated', 'Endocrine therapy plan if hormone-receptor positive'] },
    II: { label: 'Surgery + chemotherapy + radiation', weeks: 16, medical: [55000, 95000], bullets: ['Neoadjuvant or adjuvant chemotherapy (typically 4-8 cycles)', 'Breast surgery and nodal staging', 'Adjuvant radiation and endocrine / anti-HER2 therapy as indicated'] },
    III: { label: 'Multimodality: chemo ± HER2-targeted therapy, surgery, radiation', weeks: 20, medical: [95000, 180000], bullets: ['Systemic therapy first in most locally advanced cases', 'HER2-targeted therapy when the tumor is HER2-positive (major cost driver)', 'Surgery and comprehensive radiation after systemic therapy'] },
    IV: { label: 'Systemic therapy (CDK4/6, chemo, or antibody-drug conjugates)', weeks: 24, medical: [120000, 250000], bullets: ['Staging PET/CT and biopsy confirmation of metastatic disease', 'First-line systemic therapy matched to receptors', 'Supportive care and monthly monitoring'] },
  },
  lung: {
    I: { label: 'Lobectomy or SBRT for early NSCLC', weeks: 6, medical: [32000, 62000], bullets: ['Staging PET/CT and mediastinal evaluation', 'Lobectomy or stereotactic body radiation', 'Adjuvant therapy only if high-risk features'] },
    II: { label: 'Surgery + adjuvant chemotherapy', weeks: 14, medical: [55000, 95000], bullets: ['Oncologic resection at a high-volume thoracic center', 'Adjuvant platinum doublet (4 cycles typical)', 'Molecular testing for targeted adjuvant options'] },
    III: { label: 'Concurrent chemoradiation ± consolidative immunotherapy', weeks: 16, medical: [110000, 220000], bullets: ['Concurrent chemotherapy and radiation (~6 weeks)', 'Consolidative PD-L1 immunotherapy (PACIFIC-style) when eligible, CMS ASP durvalumab is a major drug cost', 'Thoracic tumor-board review before starting'] },
    IV: { label: 'Systemic therapy: chemo-immunotherapy or targeted TKI', weeks: 20, medical: [140000, 280000], bullets: ['NGS / PD-L1 testing before first-line therapy', 'Pembrolizumab 200 mg is $12,129 per dose at July 2026 CMS ASP; 8 doses ≈ $97,000 in drug cost alone', 'Supportive care and restaging every 2-3 months'] },
  },
  colorectal: {
    I: { label: 'Oncologic resection', weeks: 6, medical: [28000, 55000], bullets: ['Colectomy or proctectomy with nodal harvest', 'Pathology-driven decision on adjuvant therapy', 'Enhanced-recovery perioperative pathway'] },
    II: { label: 'Surgery ± adjuvant chemotherapy', weeks: 14, medical: [45000, 80000], bullets: ['Resection at a high-volume colorectal center', 'Adjuvant chemo for high-risk stage II', 'CEA and imaging surveillance plan'] },
    III: { label: 'Surgery + FOLFOX / CAPOX', weeks: 18, medical: [70000, 120000], bullets: ['Neoadjuvant or adjuvant oxaliplatin-based chemo (3-6 months)', 'Total mesorectal excision when rectal', 'Radiation added for most rectal tumors'] },
    IV: { label: 'Systemic therapy ± metastasectomy', weeks: 20, medical: [130000, 280000], bullets: ['RAS / BRAF / MSI testing to choose first-line therapy', 'Biologics (bevacizumab, EGFR antibodies) when indicated', 'Selective liver or lung metastasectomy at an NCI center'] },
  },
  prostate: {
    I: { label: 'Prostatectomy or definitive radiation', weeks: 8, medical: [22000, 48000], bullets: ['Shared decision: surgery vs radiation vs active surveillance', 'Robotic prostatectomy or IMRT / SBRT', 'PSA follow-up schedule'] },
    II: { label: 'Prostatectomy or radiation ± short-course ADT', weeks: 10, medical: [28000, 58000], bullets: ['Definitive local therapy', 'Androgen-deprivation therapy when NCCN intermediate-risk', 'Continence and sexual-health rehabilitation'] },
    III: { label: 'Radiation + androgen-deprivation therapy', weeks: 16, medical: [40000, 75000], bullets: ['Image-guided radiation (IMRT / brachytherapy boost)', 'ADT for 18-36 months in many high-risk cases', 'Consideration of novel anti-androgens on trial'] },
    IV: { label: 'ADT + novel hormonal therapy or chemotherapy', weeks: 20, medical: [80000, 160000], bullets: ['Androgen-deprivation backbone', 'Abiraterone, enzalutamide, or docetaxel depending on volume of disease', 'Bone-health and PSMA imaging plan'] },
  },
  pancreatic: {
    I: { label: 'Whipple or distal pancreatectomy + adjuvant chemo', weeks: 18, medical: [95000, 175000], bullets: ['High-volume pancreatic resection (Whipple when indicated)', 'Adjuvant FOLFIRINOX or gemcitabine/nab-paclitaxel', 'Nutrition and enzyme-replacement support'] },
    II: { label: 'Resection + perioperative chemotherapy', weeks: 20, medical: [110000, 190000], bullets: ['Neoadjuvant chemo is common even for borderline-resectable disease', 'Surgery at an NCI high-volume pancreas program', 'Adjuvant therapy after recovery'] },
    III: { label: 'Neoadjuvant chemo ± radiation, possible resection', weeks: 20, medical: [100000, 185000], bullets: ['FOLFIRINOX or gemcitabine/nab-paclitaxel', 'Re-staging for conversion to surgery', 'Radiation in selected locally advanced cases'] },
    IV: { label: 'Systemic chemotherapy', weeks: 16, medical: [80000, 160000], bullets: ['First-line FOLFIRINOX or gemcitabine/nab-paclitaxel', 'Molecular profiling (BRCA, KRAS, MSI)', 'Supportive care and biliary stenting when needed'] },
  },
  ovarian: {
    I: { label: 'Staging surgery ± chemotherapy', weeks: 12, medical: [45000, 85000], bullets: ['Comprehensive surgical staging by a gyn-oncologist', 'Adjuvant platinum/taxane when indicated', 'Fertility-sparing options discussed for selected stage I'] },
    II: { label: 'Debulking surgery + platinum/taxane', weeks: 16, medical: [70000, 130000], bullets: ['Primary cytoreduction at an NCI gyn-oncology program', 'Six cycles of carboplatin/paclitaxel typical', 'Bevacizumab or PARP maintenance when eligible'] },
    III: { label: 'Cytoreduction + chemo ± maintenance PARP / bevacizumab', weeks: 20, medical: [95000, 190000], bullets: ['Upfront or interval debulking surgery', 'Platinum/taxane backbone', 'Maintenance PARP inhibitor if BRCA / HRD-positive, a major pharmacy cost'] },
    IV: { label: 'Systemic therapy ± interval surgery', weeks: 20, medical: [110000, 220000], bullets: ['Neoadjuvant chemo for unresectable disease', 'Interval cytoreduction when feasible', 'Maintenance targeted therapy'] },
  },
  blood: {
    I: { label: 'Induction chemo-immunotherapy (e.g. R-CHOP)', weeks: 16, medical: [55000, 110000], bullets: ['Diagnosis confirmed with hematopathology and FISH / NGS', 'R-CHOP or equivalent for common lymphomas', 'Growth-factor support and infection prophylaxis'] },
    II: { label: 'Chemo-immunotherapy ± radiation', weeks: 18, medical: [70000, 140000], bullets: ['Stage-adapted lymphoma or CLL/ALL protocol', 'Involved-site radiation when indicated', 'Minimal residual disease monitoring'] },
    III: { label: 'Intensive therapy; transplant evaluation', weeks: 20, medical: [120000, 280000], bullets: ['Induction and consolidation at a FACT-accredited center', 'Allogeneic or autologous transplant evaluation', 'CAR-T referral if relapsed high-grade lymphoma, list prices $373k-$475k, not included unless selected later'] },
    IV: { label: 'Intensive systemic therapy ± cellular therapy pathway', weeks: 24, medical: [150000, 350000], bullets: ['Disease-specific induction (AML 7+3, ALL hyper-CVAD, etc.)', 'HLA typing and transplant / CAR-T consult', 'Extended inpatient days drive cost more than drug list price'] },
  },
  brain: {
    I: { label: 'Maximal safe resection + adjuvant radiation', weeks: 10, medical: [55000, 100000], bullets: ['Awake or image-guided craniotomy at a high-volume brain-tumor center', 'Adjuvant radiation', 'Molecular classification (IDH, 1p/19q, MGMT)'] },
    II: { label: 'Resection + radiation ± temozolomide', weeks: 14, medical: [65000, 120000], bullets: ['Maximal safe resection', 'Chemoradiation when high-grade', 'Tumor treating fields discussed for GBM'] },
    III: { label: 'Stupp protocol: surgery, radiation, temozolomide', weeks: 18, medical: [75000, 140000], bullets: ['Maximal safe resection', 'Concurrent radiation + temozolomide, then adjuvant TMZ', 'Clinical-trial screen at an NCI brain-tumor program'] },
    IV: { label: 'GBM-style multimodality care + trial screen', weeks: 18, medical: [80000, 160000], bullets: ['Surgery when safe', 'Chemoradiation and adjuvant temozolomide', 'NCI trial matching (IDH, MGMT, targeted / immunotherapy trials)'] },
  },
  head_neck: {
    I: { label: 'Single-modality surgery or radiation', weeks: 8, medical: [30000, 58000], bullets: ['Transoral or open resection with reconstruction planning', 'Definitive radiation as an alternative for selected sites', 'Speech and swallow therapy'] },
    II: { label: 'Surgery + adjuvant radiation, or chemoradiation', weeks: 12, medical: [50000, 90000], bullets: ['Nodal dissection when indicated', 'Adjuvant radiation for close margins / nodal disease', 'PEG / nutrition support during treatment'] },
    III: { label: 'Concurrent cisplatin-radiation ± surgery', weeks: 14, medical: [70000, 130000], bullets: ['HPV testing for oropharynx', 'High-dose cisplatin with IMRT (~7 weeks)', 'Dental, swallow, and feeding-tube support included in the stay plan'] },
    IV: { label: 'Chemoradiation or palliative systemic therapy', weeks: 14, medical: [80000, 160000], bullets: ['Pembrolizumab ± chemo for PD-L1-positive recurrent/metastatic disease', 'Local therapy if oligometastatic', 'Survivorship: dental, thyroid, swallow'] },
  },
  melanoma: {
    I: { label: 'Wide local excision ± sentinel node', weeks: 4, medical: [18000, 38000], bullets: ['Wide excision with appropriate margins', 'Sentinel lymph-node biopsy when indicated', 'Dermatology surveillance'] },
    II: { label: 'Surgery ± adjuvant immunotherapy', weeks: 12, medical: [40000, 120000], bullets: ['Oncologic excision and nodal staging', 'Adjuvant PD-1 therapy for high-risk stage IIB/C', 'CMS ASP pembrolizumab $12,129 per 200 mg dose'] },
    III: { label: 'Surgery + adjuvant PD-1 blockade', weeks: 16, medical: [90000, 180000], bullets: ['Therapeutic nodal dissection in selected cases', 'A year of adjuvant PD-1 is the main cost driver', 'Trial options for neoadjuvant immunotherapy'] },
    IV: { label: 'Checkpoint blockade ± targeted BRAF/MEK', weeks: 20, medical: [130000, 260000], bullets: ['BRAF testing before first-line therapy', 'PD-1 ± CTLA-4 (ipilimumab CMS ASP is high per mg)', 'Stereotactic radiation for brain mets when needed'] },
  },
  other: {
    I: { label: 'Definitive local therapy at an NCI center', weeks: 8, medical: [30000, 60000], bullets: ['Pathology review at the treating NCI center', 'Guideline-concordant surgery or radiation', 'Molecular testing to open targeted options'] },
    II: { label: 'Multimodality local + systemic therapy', weeks: 14, medical: [50000, 95000], bullets: ['NCCN-aligned combination therapy', 'Clinical-trial screen', 'Care-coordinator intake'] },
    III: { label: 'Intensive multimodality treatment', weeks: 18, medical: [80000, 160000], bullets: ['Tumor-board planned sequence of chemo / radiation / surgery', 'NCI trial matching', 'Caregiver lodging for a multi-week course'] },
    IV: { label: 'Systemic therapy and NCI trial matching', weeks: 20, medical: [110000, 230000], bullets: ['Comprehensive genomic profiling', 'Standard systemic therapy or a Phase I/II trial', 'Supportive and palliative co-management'] },
  },
};

const INSURANCE_OOP = {
  employer_ppo: { deductible: 1800, coinsurance: 0.2, cap: 4100, label: 'Typical employer PPO (KFF 2024 averages)' },
  employer_hmo: { deductible: 800, coinsurance: 0.2, cap: 3500, label: 'Typical employer HMO / EPO' },
  marketplace: { deductible: 3500, coinsurance: 0.3, cap: 10600, label: 'ACA silver-like plan, 2026 federal OOP max $10,600' },
  medicare: { deductible: 1676, coinsurance: 0.2, cap: 8000, label: 'Original Medicare Part A deductible + Part B 20% (no hard cap; estimate assumes no Medigap)' },
  medicare_advantage: { deductible: 500, coinsurance: 0.2, cap: 4500, label: 'Typical Medicare Advantage in-network MOOP' },
  medicaid: { deductible: 0, coinsurance: 0, cap: 50, label: 'Medicaid cost-sharing is minimal in most states' },
  tricare: { deductible: 150, coinsurance: 0.2, cap: 3000, label: 'TRICARE / VA, low patient cost-sharing' },
  uninsured: { deductible: 0, coinsurance: 1, cap: Infinity, label: 'Self-pay / uninsured, ask every hospital about charity care (often 40-60% off)' },
};

const TIER_MULT = { regional: 1.0, major: 1.12, destination: 1.22 };
const REGION_DISTANCE = {
  northeast: { northeast: 0, midatlantic: 1, southeast: 2, southcentral: 3, midwest: 2, mountain: 3, pacific: 3 },
  midatlantic: { northeast: 1, midatlantic: 0, southeast: 1, southcentral: 2, midwest: 1, mountain: 3, pacific: 3 },
  southeast: { northeast: 2, midatlantic: 1, southeast: 0, southcentral: 1, midwest: 2, mountain: 3, pacific: 3 },
  southcentral: { northeast: 3, midatlantic: 2, southeast: 1, southcentral: 0, midwest: 1, mountain: 2, pacific: 3 },
  midwest: { northeast: 2, midatlantic: 1, southeast: 2, southcentral: 1, midwest: 0, mountain: 2, pacific: 3 },
  mountain: { northeast: 3, midatlantic: 3, southeast: 3, southcentral: 2, midwest: 2, mountain: 0, pacific: 1 },
  pacific: { northeast: 3, midatlantic: 3, southeast: 3, southcentral: 3, midwest: 3, mountain: 1, pacific: 0 },
};
const FLIGHT_RT = [
  [150, 240],
  [240, 360],
  [350, 520],
  [420, 620],
];

export function blankIntake() {
  return {
    relation: '',
    ageGroup: 'adult',
    cancerType: '',
    stage: '',
    city: '',
    state: '',
    destination: 'match',
    insurance: '',
    lodging: '',
    companions: '2',
    urgency: 'within_month',
    memberId: '',
  };
}

export function intakeIsComplete(intake) {
  if (!intake) return false;
  return Boolean(
    intake.relation &&
    intake.ageGroup &&
    intake.cancerType &&
    intake.stage &&
    String(intake.city || '').trim() &&
    intake.state &&
    intake.destination &&
    intake.insurance &&
    intake.lodging &&
    intake.companions &&
    intake.urgency
  );
}

export function optionLabel(list, id) {
  return list.find((row) => row.id === id)?.label || id || '';
}

export function formatUSD(amount, { compact = false } = {}) {
  const n = Math.round(Number(amount) || 0);
  if (compact && n >= 1000) {
    const k = n / 1000;
    const digits = k >= 100 ? 0 : 1;
    return `$${k.toFixed(digits).replace(/\.0$/, '')}k`;
  }
  return `$${n.toLocaleString('en-US')}`;
}

export function formatUSDRange(min, max, opts) {
  return `${formatUSD(min, opts)} - ${formatUSD(max, opts)}`;
}

function protocolFor(cancerType, stage) {
  const table = PROTOCOLS[cancerType] || PROTOCOLS.other;
  const key = table[stage] ? stage : 'II';
  return table[key];
}

function hintHospital(city) {
  if (!city) return null;
  const hit = CITY_HINTS.find((row) => row.test.test(city));
  return hit ? HOSPITALS[hit.id] : null;
}

function uniqueHospitals(ids) {
  const seen = new Set();
  const out = [];
  ids.forEach((id) => {
    const h = HOSPITALS[id];
    if (!h || seen.has(h.id)) return;
    seen.add(h.id);
    out.push(h);
  });
  return out;
}

function pickThree(intake) {
  const region = STATE_REGION[intake.state] || 'midwest';
  const pediatric = intake.ageGroup === 'child';

  if (pediatric) {
    return uniqueHospitals([
      PEDIATRIC_VALUE[region] || 'chop-philadelphia',
      PEDIATRIC_BALANCED[region] || 'boston-childrens',
      PEDIATRIC_FLAGSHIP,
      'texas-childrens-houston',
      'seattle-childrens',
    ]).slice(0, 3);
  }

  if (intake.destination && CITY_DESTINATION[intake.destination]) {
    return uniqueHospitals(CITY_DESTINATION[intake.destination]);
  }

  const homeHint = hintHospital(intake.city);
  const valueId = homeHint?.id || STATE_VALUE[intake.state] || 'lurie-northwestern';
  const regional = Object.values(HOSPITALS)
    .filter((h) => !h.pediatric && h.region === region)
    .sort((a, b) => Number(a.id !== valueId) - Number(b.id !== valueId));
  const nearbyIds = regional.map((h) => h.id);
  let balancedId = intake.destination === 'nearby'
    ? (nearbyIds.find((id) => id !== valueId) || BALANCED[region])
    : BALANCED[region];
  let comfortId = intake.destination === 'nearby'
    ? (nearbyIds.find((id) => id !== valueId && id !== balancedId) || FLAGSHIP[region])
    : FLAGSHIP[region];
  if (comfortId === valueId || comfortId === balancedId) {
    comfortId = valueId === 'md-anderson-houston' ? 'msk-nyc' : 'md-anderson-houston';
  }
  if (balancedId === valueId) {
    balancedId = nearbyIds.find((id) => id !== valueId && id !== comfortId) || 'lurie-northwestern';
  }

  const extras = ['msk-nyc', 'md-anderson-houston', 'mayo-rochester', 'dfci-boston', 'hopkins-baltimore', 'city-of-hope-duarte', 'winship-atlanta', 'ucsf-helen-diller'];
  return uniqueHospitals([valueId, balancedId, comfortId, ...extras]).slice(0, 3);
}

function applyOOP(allowedMin, allowedMax, insuranceId) {
  const rule = INSURANCE_OOP[insuranceId] || INSURANCE_OOP.employer_ppo;
  const share = (allowed) => {
    if (insuranceId === 'uninsured') return allowed;
    if (insuranceId === 'medicaid') return Math.min(rule.cap, 50);
    const afterDeductible = Math.max(0, allowed - rule.deductible);
    return Math.min(rule.cap, rule.deductible + afterDeductible * rule.coinsurance);
  };
  return {
    min: Math.round(share(allowedMin)),
    max: Math.round(share(allowedMax)),
    rule,
  };
}

function lodgingMarket(city) {
  return LODGING_MARKETS[city] || LODGING_MARKETS.default;
}

function sameMetro(intake, hospital) {
  const hint = hintHospital(intake.city);
  if (hint && hint.city === hospital.city) return true;
  if (intake.state === hospital.state && hintHospital(intake.city)?.state === hospital.state) {
    const city = String(intake.city || '').toLowerCase();
    const dest = String(hospital.city || '').toLowerCase();
    if (city && dest && (city.includes(dest.split(' ')[0].toLowerCase()) || dest.includes(city.split(' ')[0]))) {
      return true;
    }
  }
  return false;
}

function travelBand(intake, hospital) {
  if (sameMetro(intake, hospital)) return [0, 0];
  const origin = STATE_REGION[intake.state] || 'midwest';
  const dest = hospital.region || STATE_REGION[hospital.state] || 'midwest';
  const hop = (REGION_DISTANCE[origin] || REGION_DISTANCE.midwest)[dest] ?? 2;
  return FLIGHT_RT[Math.min(hop, FLIGHT_RT.length - 1)];
}

function buildPlan(planType, hospital, intake, protocol) {
  const multiplier = TIER_MULT[hospital.tier] || 1;
  const medicalMin = Math.round(protocol.medical[0] * multiplier);
  const medicalMax = Math.round(protocol.medical[1] * multiplier);
  const oop = applyOOP(medicalMin, medicalMax, intake.insurance);
  const nights = Math.max(0, protocol.weeks * 6);
  const travelers = Number(intake.companions) === 1 ? 1 : 2;
  const local = sameMetro(intake, hospital);
  const stayAtHome = intake.lodging === 'home' || intake.lodging === 'family' || local && intake.lodging !== 'lodging';
  const market = lodgingMarket(hospital.city);
  const nightly = planType === 'Value' ? market.es : planType === 'Comfort' ? market.premium : market.mid;
  const lodging = stayAtHome || hospital.neverBillsFamilies ? 0 : Math.round(nightly * nights);
  const flight = travelBand(intake, hospital);
  const travelMin = hospital.neverBillsFamilies ? 0 : (local ? 80 * travelers : flight[0] * travelers);
  const travelMax = hospital.neverBillsFamilies ? 0 : (local ? 180 * travelers : flight[1] * travelers);
  const travelMid = Math.round((travelMin + travelMax) / 2);

  if (hospital.neverBillsFamilies) {
    oop.min = 0;
    oop.max = 0;
  }

  const patientMin = oop.min + lodging + travelMin;
  const patientMax = oop.max + lodging + travelMax;

  const bullets = [...protocol.bullets];
  if (hospital.neverBillsFamilies) {
    bullets.unshift('St. Jude never bills families for treatment, travel, or housing.');
  } else if (stayAtHome) {
    bullets.push(local ? 'Stay at home, local transport only' : 'Stay with family; no hotel nights in this estimate');
  } else {
    bullets.push(`${nights} nights near campus at about ${formatUSD(nightly)}/night (${planType === 'Value' ? 'extended stay' : planType === 'Comfort' ? 'hotel near campus' : 'mid-range hotel / furnished apt'})`);
  }
  if (!local && !hospital.neverBillsFamilies) {
    bullets.push(`Round-trip airfare for ${travelers === 1 ? '1 traveler' : '2 travelers'} in a typical domestic fare band`);
  }
  if (planType === 'Comfort') {
    bullets.push('Dedicated ByOnco coordinator, records transfer, and second-opinion slot at the destination center');
  } else if (planType === 'Balanced') {
    bullets.push('ByOnco coordinator for scheduling, records, and insurance pre-auth checklist');
  } else {
    bullets.push('ACS Hope Lodge or hospital hospitality housing screened when a room is available (often $0-$25/night)');
  }

  const overBudget = intake.insurance === 'uninsured' && patientMax > 80000 && planType === 'Comfort';

  return {
    planType,
    icon: planType === 'Value' ? 'lucide:map-pin' : planType === 'Comfort' ? 'lucide:sparkles' : 'lucide:building-2',
    city: hospital.city,
    region: hospital.state,
    state: hospital.state,
    country: 'United States',
    hospitalId: hospital.id,
    hospitalName: hospital.name,
    hospitalNote: hospital.note,
    designation: hospital.designation,
    website: hospital.website,
    duration: `~${protocol.weeks} weeks`,
    weeks: protocol.weeks,
    protocol: protocol.label,
    priceRange: formatUSDRange(patientMin, patientMax),
    priceRangeMin: patientMin,
    priceRangeMax: patientMax,
    currency: 'USD',
    medicalAllowed: formatUSDRange(medicalMin, medicalMax),
    medicalAllowedMin: medicalMin,
    medicalAllowedMax: medicalMax,
    patientOOP: formatUSDRange(oop.min, oop.max),
    patientOOPMin: oop.min,
    patientOOPMax: oop.max,
    lodgingCost: lodging,
    travelCost: formatUSDRange(travelMin, travelMax),
    travelCostMid: travelMid,
    nights: stayAtHome ? 0 : nights,
    travelers,
    localCare: local,
    bullets,
    fitNote: fitNote(planType, hospital, intake, local, stayAtHome),
    overBudget,
    costBreakdown: {
      hospitalFees: formatUSDRange(Math.round(medicalMin * 0.55), Math.round(medicalMax * 0.55)),
      drugsAndInfusion: formatUSDRange(Math.round(medicalMin * 0.28), Math.round(medicalMax * 0.32)),
      diagnostics: formatUSDRange(Math.round(medicalMin * 0.08), Math.round(medicalMax * 0.08)),
      travel: formatUSDRange(travelMin, travelMax),
      lodging: lodging ? formatUSD(lodging) : 'Not needed',
      patientShare: formatUSDRange(oop.min, oop.max),
    },
    timeline: [
      { week: 'Week 0-1', phase: 'Records & insurance', tasks: ['Collect pathology, imaging, and insurance card', 'Submit records to the NCI center', 'Benefits check and prior-auth checklist'] },
      { week: 'Week 2-3', phase: 'Consult', tasks: ['New-patient visit (in person or video)', 'Repeat staging only if images are incomplete', 'Tumor-board treatment recommendation'] },
      { week: `Week 3-${protocol.weeks}`, phase: 'Active treatment', tasks: [protocol.label, stayAtHome ? 'Stay local' : `Caregiver lodging near ${hospital.city}`, 'Weekly labs / on-treatment visits'] },
      { week: `Week ${protocol.weeks}-${protocol.weeks + 2}`, phase: 'Recovery', tasks: ['Transition back to a local oncologist', 'Survivorship or maintenance plan', 'ByOnco follow-up check-in'] },
    ],
    packageInclusions: [
      'Guideline-based protocol estimate at an NCI-level centre',
      'Patient out-of-pocket band after typical insurance rules',
      'Travel and lodging estimate for the treatment window',
      'ByOnco coordinator handoff within 48 hours of request',
      'Records + prior-auth checklist',
      'Optional add-ons (lodging upgrade, transfers, concierge)',
    ],
    fulfillment: {
      mode: 'live_quote_plus_coordinator',
      paymentAtRequest: false,
      slaHours: 24,
      note: 'CMS price-transparency quote + eligibility when member ID is provided; coordinator confirms final hospital numbers and locks the slot.',
    },
  };
}

function fitNote(planType, hospital, intake, local, stayAtHome) {
  if (hospital.neverBillsFamilies) {
    return 'Best when the patient is a child: St. Jude covers treatment, travel, and housing so the family bill is $0.';
  }
  if (planType === 'Value') {
    return local
      ? 'Closest NCI-level option to home, lowest travel and lodging, same guideline-based protocol.'
      : 'Regional NCI center that keeps travel shorter. Ask about Hope Lodge or hospital hospitality housing.';
  }
  if (planType === 'Comfort') {
    return 'National destination center for complex or highly specialized care. Highest travel and hotel cost, strongest trial access.';
  }
  if (stayAtHome) {
    return 'Strong NCI program without adding hotel nights, because you can stay at home or with family.';
  }
  return 'High-volume NCI comprehensive center with a middle lodging tier and a coordinator on the file.';
}

function suggestionsFor(intake, plans) {
  const items = [];
  const comfort = plans.find((p) => p.planType === 'Comfort');
  const value = plans.find((p) => p.planType === 'Value');
  if (intake.lodging === 'lodging') {
    items.push({
      title: 'Hospitality housing',
      text: 'American Cancer Society Hope Lodge and hospital hospitality houses often run $0-$25/night. If a room is available, the Value plan lodging line can drop by several thousand dollars.',
    });
  }
  if (intake.insurance === 'uninsured') {
    items.push({
      title: 'Charity care',
      text: 'Every NCI center in this list has a financial-assistance office. Self-pay discounts of 40-60% off charges are common, and Medicaid emergency coverage can apply after diagnosis. Ask before the first infusion.',
    });
  }
  if (intake.insurance === 'medicare') {
    items.push({
      title: 'Medigap',
      text: 'Original Medicare has no hard OOP cap on Part B. A Medigap plan (or switching the estimate to Medicare Advantage) is usually what keeps a 20% coinsurance from running into five figures.',
    });
  }
  if (comfort && value && comfort.priceRangeMax > value.priceRangeMax * 1.4) {
    items.push({
      title: 'Start local, escalate if needed',
      text: `Get the first consult at ${value.hospitalName} while records are also sent to ${comfort.hospitalName}. Many families only travel to a destination center if the local NCI team recommends it.`,
    });
  }
  items.push({
    title: 'Clinical trials',
    text: 'Ask each center to screen ClinicalTrials.gov matches for this diagnosis. Trial care can shift drug cost to the sponsor; it does not always lower hospital facility fees.',
  });
  return { items };
}

export function buildJourneyPlans(intake) {
  const complete = intakeIsComplete(intake);
  if (!complete) {
    return { ok: false, error: 'Fill in every blank to see three plans.' };
  }

  const protocol = protocolFor(intake.cancerType, intake.stage === 'unknown' ? 'II' : intake.stage);
  const hospitals = pickThree(intake);
  const types = ['Value', 'Balanced', 'Comfort'];
  const plans = hospitals.map((hospital, idx) => buildPlan(types[idx] || 'Balanced', hospital, intake, protocol));

  const relation = optionLabel(RELATIONS, intake.relation);
  const cancer = optionLabel(CANCER_TYPES, intake.cancerType);
  const stageLabel = optionLabel(STAGES, intake.stage);
  const stateLabel = optionLabel(US_STATES, intake.state);
  const destLabel = optionLabel(DESTINATIONS, intake.destination);
  const cover = optionLabel(INSURANCE_TYPES, intake.insurance);

  const profile = {
    cancerType: cancer.replace(/^./, (c) => c.toUpperCase()),
    stage: stageLabel,
    originDest: `${intake.city.trim()}, ${intake.state} → ${destLabel}`,
    estBudget: cover,
    urgencyLevel: optionLabel(URGENCY, intake.urgency),
    recommendedProtocol: protocol.label,
    insurance: cover,
    home: `${intake.city.trim()}, ${stateLabel}`,
  };

  const summary = `Based on ${stageLabel.toLowerCase()} ${cancer} and ${cover}, here are three NCI-center journeys from ${intake.city.trim()}, ${intake.state}. Figures are US-dollar estimates: your out-of-pocket after insurance, plus travel and stay. Confirm them with the hospital before you travel.`;

  return {
    ok: true,
    profile,
    plans,
    suggestions: suggestionsFor(intake, plans),
    disclaimer: 'ByOnco helps you plan care journeys with public cost sources from CMS, KFF, JAMA, and ACS/Milliman. Confirm every number with the hospital and your insurer before you travel.',
    sources: JOURNEY_SOURCES,
    summary,
    relation,
    intake,
  };
}

export function saveJourneyResult(result) {
  try {
    sessionStorage.setItem(JOURNEY_STORAGE_KEY, JSON.stringify(result));
  } catch {
    /* ignore quota */
  }
}

export function loadJourneyResult() {
  try {
    const raw = sessionStorage.getItem(JOURNEY_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function prefillFromProfile(profile) {
  const intake = blankIntake();
  if (!profile) return intake;
  const relationMap = {
    Self: 'self', Father: 'parent', Mother: 'parent', Spouse: 'spouse',
    Son: 'child', Daughter: 'child', Grandfather: 'parent', Grandmother: 'parent',
    Relative: 'relative', Other: 'friend',
  };
  if (profile.relationToPatient) intake.relation = relationMap[profile.relationToPatient] || intake.relation;
  if (profile.isSelfPatient) intake.relation = 'self';
  const cancer = String(profile.cancerType || '').toLowerCase();
  const cancerHit = CANCER_TYPES.find((row) => cancer.includes(row.id) || cancer.includes(row.label.replace(' cancer', '')));
  if (cancerHit) intake.cancerType = cancerHit.id;
  const stage = String(profile.cancerStage || '');
  if (/iv|4/i.test(stage)) intake.stage = 'IV';
  else if (/iii|3/i.test(stage)) intake.stage = 'III';
  else if (/ii|2/i.test(stage)) intake.stage = 'II';
  else if (/i|1|0/i.test(stage)) intake.stage = 'I';
  if (profile.city) intake.city = profile.city;
  const st = String(profile.state || '').toUpperCase();
  if (US_STATES.some((row) => row.id === st)) intake.state = st;
  else {
    const named = US_STATES.find((row) => row.label.toLowerCase() === String(profile.state || '').toLowerCase());
    if (named) intake.state = named.id;
  }
  if (profile.urgency) intake.urgency = ['immediately', 'within_month', 'flexible'].includes(profile.urgency)
    ? profile.urgency
    : intake.urgency;
  return intake;
}
